import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import React, { useCallback, useMemo } from 'react';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { ReportFilterTab, useGetFinancialTransactionTotalsQuery } from 'shared/types/graphql';
import { PaymentReportTotalTypes } from '../enums';
import { IColumnSelection, PaymentReportFilters } from '../types';
import { formatMoney } from '../utils/formatMoney';

interface TotalsBannerProps {
  clinicId: string;
  payoutId?: string;
  filters: PaymentReportFilters;
  selectedFilterTab: ReportFilterTab;
  disbursementsColumnsSelection: IColumnSelection[];
  transactionsColumnsSelection: IColumnSelection[];
}

const totalsCategories = [
  {
    type: PaymentReportTotalTypes.Amount,
    label: 'AMOUNT',
  },
  {
    type: PaymentReportTotalTypes.ClientFee,
    label: 'CLIENT FEES',
  },
  {
    type: PaymentReportTotalTypes.TotalInvoiced,
    label: 'TOTAL INVOICED',
  },
  {
    type: PaymentReportTotalTypes.TransactionFees,
    label: 'TRANSACTION FEES',
  },
  {
    type: PaymentReportTotalTypes.NetProceeds,
    label: 'NET PROCEEDS',
  },
];

export const TotalsTable = ({
  clinicId,
  payoutId,
  filters: {
    nameFiltering,
    cardBrandFiltering,
    cardTypeFiltering,
    excludedTerminalNames,
    selectionRange,
    showSubscriptions,
  },
  selectedFilterTab,
  disbursementsColumnsSelection,
  transactionsColumnsSelection,
}: TotalsBannerProps): JSX.Element => {
  const shouldDisplayTotal = useCallback(
    (totalType: PaymentReportTotalTypes): boolean => {
      const columnsSelection =
        selectedFilterTab === ReportFilterTab.Disbursements
          ? disbursementsColumnsSelection
          : transactionsColumnsSelection;
      const column = columnsSelection.filter((column) => column.selector === totalType)?.[0];

      if (column?.selector === totalType) {
        return !column.omit;
      }

      return false;
    },
    [disbursementsColumnsSelection, selectedFilterTab, transactionsColumnsSelection],
  );

  const areAnyFilteredTotalSelected = useMemo(
    () =>
      shouldDisplayTotal(PaymentReportTotalTypes.Amount) ||
      shouldDisplayTotal(PaymentReportTotalTypes.ClientFee) ||
      shouldDisplayTotal(PaymentReportTotalTypes.TransactionFees) ||
      shouldDisplayTotal(PaymentReportTotalTypes.NetProceeds) ||
      shouldDisplayTotal(PaymentReportTotalTypes.TotalInvoiced),
    [shouldDisplayTotal],
  );

  const { data: totalsData, loading: isLoadingTotals } = useGetFinancialTransactionTotalsQuery({
    variables: {
      data: {
        clinicId,
        includeSubscriptions: showSubscriptions,
        reportFilterTab: selectedFilterTab,
        ...(!payoutId && {
          timestamp: {
            startDate: selectionRange.startDate.toISOString(),
            endDate: selectionRange.endDate.toISOString(),
          },
        }),
        ...(payoutId && { stripePayoutId: payoutId }),
        clientName: nameFiltering,
        cardBrand: cardBrandFiltering !== 'All' ? cardBrandFiltering : undefined,
        cardType: cardTypeFiltering !== 'All' ? cardTypeFiltering : undefined,
        terminalNames: excludedTerminalNames,
      },
    },
    skip: !clinicId.length || !areAnyFilteredTotalSelected,
  });

  const tableTotals: Record<PaymentReportTotalTypes, number> = useMemo(() => {
    const data = totalsData?.getFinancialTransactionTotals;
    return {
      [PaymentReportTotalTypes.Amount]: data?.amount || 0,
      [PaymentReportTotalTypes.ClientFee]: data?.clientFees || 0,
      [PaymentReportTotalTypes.TotalInvoiced]: data?.invoiced || 0,
      [PaymentReportTotalTypes.TransactionFees]: data?.transactionFees ? data.transactionFees : 0,
      [PaymentReportTotalTypes.NetProceeds]: data?.netProceeds || 0,
    };
  }, [totalsData?.getFinancialTransactionTotals]);

  if (!areAnyFilteredTotalSelected) {
    return <></>;
  }

  return (
    <Flex className="PaymentReport__TotalsTable" py={3} px={0} justifyContent="flex-end">
      <Flex justifyContent="flex-end" alignItems="center">
        <Text size="sm">Filtered Totals</Text>
        <Box as="table" ml={3}>
          <Box as="tbody">
            <Box as="tr">
              {totalsCategories.map((category) => {
                return shouldDisplayTotal(category.type) ? (
                  <React.Fragment key={`totals_${category.type}`}>
                    <Box as="th">
                      <Text size="sm" fontWeight="normal" mx={1}>
                        {category.label}
                      </Text>
                    </Box>
                    <Box as="td" px={2} bgColor="background.default">
                      {isLoadingTotals ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Text size="sm">{formatMoney(tableTotals[category.type])}</Text>
                      )}
                    </Box>
                  </React.Fragment>
                ) : null;
              })}
            </Box>
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
};
